import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * «Ahora 14:32 — Próximos vuelos»: a departures board's one strong line. Above it, what
 * should have left by now; below it, the flights still to come. `none`: nothing is left
 * to leave in the board's window (`noneLabel` says it in the board's own words).
 *
 * Shared by /flights and step 1 of the declare flow (PO 2026-11-04: «la línea divisoria
 * de "ahora" que ya tenemos en la lista de vuelos»). Sticky under each board's own
 * header row, whose height the caller passes as `className` (`top-…`).
 */
export const NowLine = forwardRef<
  HTMLLIElement,
  { now: number; none: boolean; noneLabel?: string; className?: string; testId?: string }
>(function NowLine({ now, none, noneLabel, className, testId = 'board-now-line' }, ref) {
  const { t } = useTranslation('network');
  const time = new Date(now).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return (
    <li
      ref={ref}
      className={cn(
        'sticky z-[5] flex items-center gap-2 border-y-2 border-primary bg-primary/10 px-3 py-1 backdrop-blur',
        className ?? 'top-7',
      )}
      data-testid={testId}
      data-none={none || undefined}
    >
      <span className="rounded-full bg-primary px-2 py-0.5 font-mono text-[11px] font-bold tabular-nums text-primary-foreground">
        {t('board.now', { time })}
      </span>
      <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-primary">
        {none ? null : <ArrowDown className="h-3 w-3" aria-hidden />}
        {none ? (noneLabel ?? t('board.noneLeft')) : t('board.upcoming')}
      </span>
    </li>
  );
});
